import { SERVER_PROVIDER_ID } from '../../core/providers';
import { getDb } from '../../core/db';
import type { ChatMessage, Preset, ProviderConfig } from '../../core/types';
import type { GeneratedImage } from '../../core/image';
import { messageRepo } from '../../core/repositories';
import { listProviders } from '../../core/repositories/providerRepo';
import { readSecret } from '../providers/secretStore';
import { resolveSelectedImageEntry } from '../chat/imageEntry';
import type { ImageGenerator } from '../chat/imageEntry';
import type { OfficialImageOption } from '../chat/officialModelCatalog';
import { saveGeneratedImages } from '../gallery/save';
import type { SaveGeneratedImagesOptions, SaveGeneratedImagesResult } from '../gallery/save';
import { mimoClient } from '../mimo/client';
import { loadTtsCatalog } from '../mimo/catalog';
import { modelGateway } from '../modelGateway';
import { registerRoleImageDelivery } from '../chat/imageJobs';
import { getEntitlements } from '../subscription/entitlements';
import type { ParsedRichOutput } from './richOutput';
import { automationAllowsBubble, automationAllowsMoment } from './richOutput';
import { shiftOutOfQuietPeriod } from './quietHours';
import { scheduleAutomation } from './eventRepo';
import { createMoment } from '../moments/momentRepo';
import { getMomentAccountId, subscribeMomentAccount } from '../moments/accountScope';
import { roleVoiceError, roleVoiceProviderError, roleVoiceStore } from '../mimo/roleVoiceStore';

export interface RichMediaDependencies {
  providers: readonly ProviderConfig[];
  officialImageOptions: readonly OfficialImageOption[];
  signal?: AbortSignal;
  expectedAccountId?: string | null;
  generateImage?: ImageGenerator;
  saveImages?: (
    images: readonly GeneratedImage[],
    options: SaveGeneratedImagesOptions,
  ) => Promise<SaveGeneratedImagesResult>;
}

export interface RichMediaResult {
  message: ChatMessage | undefined;
  errors: string[];
  imagesAdded: number;
  audioAdded: boolean;
}

export function roleImageAvailable(preset: Preset, deps: Pick<RichMediaDependencies, 'providers' | 'officialImageOptions'>): boolean {
  if (!preset.autoImageEnabled || !preset.imageProviderId?.trim() || !preset.imageModel?.trim()) return false;
  const entry = resolveSelectedImageEntry({
    providerId: preset.imageProviderId,
    model: preset.imageModel,
    providers: deps.providers,
    officialOptions: deps.officialImageOptions,
  });
  return entry.config !== null || entry.serverModel !== null;
}

function blobDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('音频读取失败'));
    reader.onerror = () => reject(new Error('音频读取失败'));
    reader.readAsDataURL(blob);
  });
}

export async function generateRoleImage(
  prompt: string,
  preset: Preset,
  deps: RichMediaDependencies,
): Promise<GeneratedImage[]> {
  if (!preset.imageProviderId || !preset.imageModel) throw new Error('角色卡未配置生图提供商或模型');
  const resolved = resolveSelectedImageEntry({
    providerId: preset.imageProviderId,
    model: preset.imageModel,
    providers: deps.providers,
    officialOptions: deps.officialImageOptions,
  });
  if (resolved.serverModel) {
    return modelGateway.generateImage({
      serverModel: resolved.serverModel,
      prompt,
      automation: true,
      ...(deps.signal ? { signal: deps.signal } : {}),
    });
  }
  if (!resolved.config) throw new Error(resolved.hint);
  const request = {
    prompt,
    signal: deps.signal ?? new AbortController().signal,
    config: resolved.config,
  };
  return deps.generateImage
    ? deps.generateImage(request)
    : modelGateway.generateImage({ config: request.config, prompt, signal: request.signal });
}

async function synthesizeRoleAudio(text: string, preset: Preset, messageId: string, signal: AbortSignal, accountId: string | null): Promise<Blob> {
  if (!preset.ttsProviderId || !preset.ttsModel) throw new Error('角色卡未配置语音提供商或模型');
  const providerError = roleVoiceProviderError(preset);
  if (providerError) throw new Error(providerError);
  if (preset.ttsProviderId === SERVER_PROVIDER_ID) {
    const catalog = await loadTtsCatalog();
    const selected = catalog.find((model) => model.id === preset.ttsModel && model.available);
    if (!selected) throw new Error('角色语音模型不可用');
    const account = await mimoClient.account(signal);
    if (signal.aborted || getMomentAccountId() !== accountId || account.userId !== accountId) throw new DOMException('Aborted', 'AbortError');
    if (!account.allowed && !account.active) throw new Error('当前官方套餐不支持语音功能');
    const profile = selected.mode === 'preset' ? undefined : await roleVoiceStore.read(account.userId, preset.id);
    const configurationError = roleVoiceError(selected.mode, profile, preset.ttsAutoVoiceDesign === true);
    if (configurationError) throw new Error(configurationError);
    if (signal.aborted || getMomentAccountId() !== accountId) throw new DOMException('Aborted', 'AbortError');
    const adapter = modelGateway.createOfficialTtsAdapter({
      getSelection: () => ({
        userId: account.userId,
        mode: selected.mode,
        preferences: account.preferences,
      }),
      getMessageId: () => messageId,
      ...(selected.mode !== 'preset' ? { localVoice: {
        description: (selected.mode === 'design' ? profile?.designDescription : profile?.cloneDescription) ?? '',
        ...(profile?.reference ? { reference: profile.reference } : {}),
        autoVoiceDesign: preset.ttsAutoVoiceDesign === true,
        preset,
      } } : {}),
    });
    return (await adapter.speak({ text, messageId, signal })).audio;
  }
  const provider = preset.ttsProviderId;
  return (async () => {
    const config = listProviders().then((rows) => rows.find((row) => row.id === provider && row.enabled));
    const row = await config;
    if (!row) throw new Error('角色语音提供商已停用或删除');
    const apiKey = readSecret(row.apiKeyRef);
    return (await modelGateway.speak({
      config: {
        id: `${row.id}#tts`,
        kind: 'openai-compatible',
        name: row.name,
        baseUrl: row.baseUrl,
        apiKeyRef: row.apiKeyRef,
        defaultModel: preset.ttsModel,
        defaultVoice: 'alloy',
        enabled: true,
        ...(apiKey ? { apiKey } : {}),
      },
      request: { text, model: preset.ttsModel, messageId, signal },
    })).audio;
  })();
}

type RoleVoiceResult = Pick<RichMediaResult, 'message' | 'errors' | 'audioAdded'>;
const momentDeliveries = new WeakMap<ReturnType<typeof getDb>, Map<string, Promise<string[]>>>();

async function publishRoleMoments(preset: Preset, message: ChatMessage, parsed: ParsedRichOutput, deps: RichMediaDependencies): Promise<string[]> {
  const db = getDb();
  const userId = deps.expectedAccountId === undefined ? getMomentAccountId() : deps.expectedAccountId;
  if (getMomentAccountId() !== userId) return [];
  const key = `${userId ?? 'local'}:role-post:${message.id}`;
  let pending = momentDeliveries.get(db);
  if (!pending) { pending = new Map(); momentDeliveries.set(db, pending); }
  const existing = pending.get(key);
  if (existing) return existing;
  const delivery = (async () => {
    const errors: string[] = [];
    // One post per reply; the stable ID makes recovery and parallel tabs idempotent.
    const draft = parsed.momentPublications?.[0];
    if (!draft) return errors;
    const saved = await messageRepo.getMessage(message.id);
    if (saved?.momentPublishedId === key || await db.moments.get(key)) {
      if (saved?.status === 'done' && !saved.content.trim() && getMomentAccountId() === userId) {
        await messageRepo.updateMessage(message.id, { content: '已发布朋友圈。' });
      }
      return errors;
    }
    const current = saved;
    if (current?.role !== 'assistant' || current.status !== 'done' || deps.signal?.aborted
      || (current.backgroundJob && current.backgroundJob.userId !== userId)) return errors;
    const images: string[] = [];
    const entitlements = getEntitlements();
    if (!entitlements.active || !entitlements.capabilities.autoText) return errors;
    if (preset.autoImageEnabled && entitlements.capabilities.autoImage) {
      for (const prompt of draft.imagePrompts) {
        try { images.push(...(await generateRoleImage(prompt, preset, deps)).map(image => image.dataUrl)); }
        catch (error) {
          if (deps.signal?.aborted) return errors;
          errors.push(error instanceof Error ? error.message : '朋友圈配图生成失败');
        }
      }
    }
    if (deps.signal?.aborted || getMomentAccountId() !== userId) return errors;
    const latestEntitlements = getEntitlements();
    if (!latestEntitlements.active || !latestEntitlements.capabilities.autoText) return errors;
    await db.transaction('rw', db.moments, db.messages, async () => {
      const latest = await db.messages.get(message.id);
      if (latest?.status !== 'done' || latest.momentPublishedId === key || await db.moments.get(key)) return;
      await db.moments.add({ ...createMoment({ id: key, preset, content: draft.content,
        images: latestEntitlements.capabilities.autoImage ? images : [],
        sourceMessageId: message.id }), ...(userId ? { userId } : {}) });
      await db.messages.update(message.id, { momentPublishedId: key,
        ...(!latest.content.trim() ? { content: '已发布朋友圈。' } : {}) });
    });
    return errors;
  })();
  pending.set(key, delivery);
  try { return await delivery; }
  finally { pending.delete(key); }
}

const voiceDeliveries = new WeakMap<ReturnType<typeof getDb>, Map<string, Promise<RoleVoiceResult>>>();

export function roleVoiceDeliveryActive(messageId: string): boolean {
  return voiceDeliveries.get(getDb())?.has(messageId) === true;
}

/** Foreground delivery and background recovery share one synthesis per message. */
export async function deliverRoleVoice(preset: Preset, message: ChatMessage, signal?: AbortSignal, requested = false): Promise<RoleVoiceResult> {
  const db = getDb();
  let pending = voiceDeliveries.get(db);
  if (!pending) { pending = new Map(); voiceDeliveries.set(db, pending); }
  const existing = pending.get(message.id);
  if (existing) return existing;
  const accountId = getMomentAccountId();
  const controller = new AbortController();
  const abort = (): void => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) controller.abort();
  const activeSignal = controller.signal;
  const unsubscribe = subscribeMomentAccount(() => { if (getMomentAccountId() !== accountId) controller.abort(); });
  const delivery = (async (): Promise<RoleVoiceResult> => {
    let current = await messageRepo.getMessage(message.id);
    const result = (): RoleVoiceResult => ({ message: current, errors: [], audioAdded: Boolean(current?.audioUrl) });
    const entitlements = getEntitlements();
    if (!current || activeSignal.aborted || current.role !== 'assistant' || current.status !== 'done'
      || (current.backgroundJob && current.backgroundJob.userId !== accountId)
      || !preset.autoTtsEnabled || !entitlements.active || !entitlements.capabilities.autoTts) {
      return result();
    }
    if (requested && current.roleVoiceRequested !== true) current = await messageRepo.updateMessage(current.id, { roleVoiceRequested: true });
    if (current?.roleVoiceRequested !== true) return result();
    if (current.audioUrl) return result();
    if (current.content.trim() === '') {
      const audioError = '语音工具缺少可朗读内容';
      current = await messageRepo.updateMessage(current.id, { audioError });
      return { message: current, errors: [audioError], audioAdded: false };
    }
    const text = current.content;
    try {
      current = await messageRepo.updateMessage(current.id, { audioSending: true, audioError: undefined });
      if (!current || activeSignal.aborted) return result();
      const blob = await synthesizeRoleAudio(text, preset, current.id, activeSignal, accountId);
      if (blob.size > 8 * 1024 * 1024) throw new Error('语音文件过大，未能附加');
      const audioUrl = await blobDataUrl(blob);
      current = await messageRepo.getMessage(message.id);
      if (!current || activeSignal.aborted || current.content !== text || current.status !== 'done') return result();
      if (!current.audioUrl) current = await messageRepo.updateMessage(current.id, { audioUrl, audioError: undefined });
      return result();
    } catch (error) {
      current = await messageRepo.getMessage(message.id);
      if (!current || activeSignal.aborted || current.content !== text || current.status !== 'done') return result();
      const audioError = error instanceof Error ? error.message : '自动语音失败';
      current = await messageRepo.updateMessage(current.id, { audioError });
      return { message: current, errors: [audioError], audioAdded: false };
    } finally {
      await messageRepo.updateMessage(message.id, { audioSending: false });
    }
  })().finally(() => { unsubscribe(); signal?.removeEventListener('abort', abort); });
  pending.set(message.id, delivery);
  try { return await delivery; }
  finally { pending.delete(message.id); }
}

/**
 * Strip markers already stored in `message.content`, then deliver media that
 * the role is allowed to send and persist any future automation markers.
 */
export async function applyRichOutput(
  preset: Preset,
  message: ChatMessage,
  parsed: ParsedRichOutput,
  deps: RichMediaDependencies,
): Promise<RichMediaResult> {
  const imageAccountId = getMomentAccountId();
  if (deps.expectedAccountId !== undefined && getMomentAccountId() !== deps.expectedAccountId) {
    return { message, errors: [], imagesAdded: 0, audioAdded: false };
  }
  const errors: string[] = [];
  let next = message;
  const entitlements = getEntitlements();
  const autoImage = entitlements.active && entitlements.capabilities.autoImage && preset.autoImageEnabled === true;
  const autoText = entitlements.active && entitlements.capabilities.autoText;

  if (autoText && parsed.momentPublications?.length) {
    errors.push(...await publishRoleMoments(preset, message, parsed, deps));
    next = await messageRepo.getMessage(message.id) ?? next;
  }

  const voice = await deliverRoleVoice(preset, next, deps.signal, parsed.ttsRequested);
  if (voice.message) next = voice.message;
  errors.push(...voice.errors);

  if (parsed.imagePrompts.length > 0 && autoImage) {
    const releaseDelivery = registerRoleImageDelivery(next.id);
    try {
      next = await messageRepo.updateMessage(next.id, { imageSending: true }) ?? next;
      const generated: GeneratedImage[] = [];
      for (const prompt of parsed.imagePrompts.slice(0, 3)) {
        try {
          generated.push(...await generateRoleImage(prompt, preset, deps));
        } catch (error) {
          errors.push(error instanceof Error ? error.message : '自动生图失败');
        }
      }
      if (generated.length > 0) {
        if (getMomentAccountId() !== imageAccountId) return { message: next, errors, imagesAdded: 0, audioAdded: false };
        await (deps.saveImages ?? saveGeneratedImages)(generated, {
          prompt: parsed.imagePrompts.join('\n'),
          model: preset.imageModel ?? 'role-image',
          presetId: preset.id,
          conversationId: message.conversationId,
          ...(imageAccountId === null ? {} : { userId: imageAccountId }),
        });
        if (getMomentAccountId() !== imageAccountId) return { message: next, errors, imagesAdded: 0, audioAdded: false };
        next = await messageRepo.updateMessage(next.id, {
          images: [...(next.images ?? []), ...generated.map((image) => image.dataUrl)],
        }) ?? next;
      }
    } finally {
      try { next = await messageRepo.updateMessage(next.id, { imageSending: false }) ?? next; }
      finally { releaseDelivery(); }
    }
  }

  if (autoText && automationAllowsBubble(preset.automationMode) && parsed.bubbleDelayMinutes !== null) {
    const dueAt = shiftOutOfQuietPeriod(
      new Date(Date.now() + parsed.bubbleDelayMinutes * 60_000),
      preset.quietPeriods ?? [],
    ).getTime();
    await scheduleAutomation({ presetId: preset.id, kind: 'bubble', dueAt, sourceMessageId: message.id });
  }
  if (autoText && automationAllowsMoment(preset.automationMode) && parsed.momentDelayMinutes !== null) {
    await scheduleAutomation({
      presetId: preset.id,
      kind: 'moment',
      dueAt: Date.now() + parsed.momentDelayMinutes * 60_000,
      sourceMessageId: message.id,
    });
  }
  if (autoText && automationAllowsMoment(preset.automationMode) && parsed.momentCheckDelayMinutes != null) {
    await scheduleAutomation({ presetId: preset.id, kind: 'moment_check',
      dueAt: Date.now() + parsed.momentCheckDelayMinutes * 60_000, sourceMessageId: message.id });
  }

  return { message: next, errors, imagesAdded: (next.images?.length ?? 0) - (message.images?.length ?? 0), audioAdded: voice.audioAdded };
}
